import { useFormatter } from '@api/useFormatter'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { useSupplyManagementContext } from '@components/SupplyManagement/contexts/SupplyManagementContext'
import { GraviGrid } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { useEffect, useMemo, useRef, useState } from 'react'

import { usePrices } from '@/api/usePrices'
import { gridViewManager } from '@/utils/grid'
import { PriceManagementActionButtons } from '../ActionButtons'
import { PriceGrouping } from './CellRenderers/PriceGrouping'
import { getColumnDefs } from './columnDefs'

type SupplyManagementGridProps = {
  openHistoryDrawer: (rowHistory: Gravitate.SupplyManagementOption) => void
}

function canShowCarrierView(scopes: Partial<Gravitate.AuthorizationResponse>) {
  const canViewPriceManagement = scopes?.supply_directive?.price_management?.view
  const canEditPriceManagement = scopes?.supply_directive?.price_management?.edit && !scopes.is_carrier
  return scopes?.is_carrier || (canViewPriceManagement && !canEditPriceManagement) || !scopes?.price_visibility
}

const SUPPLY_DIRECTORY_GRID_KEY = 'supply-directory'

export function SupplyDirectoryGrid({ openHistoryDrawer }: SupplyManagementGridProps) {
  const { updateSetting } = useUserSettings()
  const { scopes } = useSecurityContext()
  const { formatCurrency } = useFormatter()
  const {
    supplyDirectoryData,
    isSupplyDirectoryFetching,
    isSupplyDirectoryRefetching,
    openEditOutage,
    cancelOutage,
    handleUpdateHeaderFilters,
    headerFilters,
    supplyDirectoryGridFilters,
    setSupplyDirectoryGridFilters,
  } = useSupplyManagementContext()
  const { updateSupplyRowMutation } = usePrices()
  const { mutateAsync: handleUpdateRow } = updateSupplyRowMutation()

  const [expandedGroups, setExpandedGroups] = useState(['all'])
  const [isExpanded, setIsExpanded] = useState(true)

  const gridAPIRef = useRef(null)
  const columnApiRef = useRef(null)

  const showCarrierView = useMemo(() => canShowCarrierView(scopes), [scopes])
  const columnDefs = useMemo(
    () =>
      getColumnDefs(
        showCarrierView,
        openHistoryDrawer,
        formatCurrency(null, true),
        openEditOutage,
        cancelOutage,
        headerFilters
      ),
    [showCarrierView, headerFilters]
  )
  const agPropOverrides = useMemo(
    () => ({
      groupDisplayType: 'groupRows',
      groupRowRendererParams: {
        innerRenderer: PriceGrouping,
      },
      getRowId: (row) => row.data?.id,
    }),
    []
  )
  const normalizedData: Gravitate.SupplyManagementOption = useMemo(() => {
    if (supplyDirectoryData?.pricing_rows?.length !== 0) {
      return supplyDirectoryData?.pricing_rows.flatMap((group) =>
        group.data.flatMap((item) => [item, ...item.children])
      )
    }
    return []
  }, [supplyDirectoryData?.pricing_rows])

  useEffect(() => {
    if (columnApiRef?.current) {
      if (headerFilters?.groupBy === 'site') {
        columnApiRef?.current?.setRowGroupColumns(['product'])
        columnApiRef?.current?.applyColumnState({
          state: [
            { colId: 'price', sort: null },
            { colId: 'total_price', sort: 'asc' }
          ],
          defaultState: { sort: null },
        })
      } else {
        columnApiRef?.current?.setRowGroupColumns([headerFilters?.groupBy, 'product'])
        columnApiRef?.current?.applyColumnState({
          state: [
            { colId: 'total_price', sort: null },
            { colId: 'price', sort: 'asc' }
          ],
          defaultState: { sort: null },
        })
      }
      columnApiRef.current.setColumnsVisible(
        ['total_price', 'freight', 'loaded_miles', 'loaded_minutes'],
        headerFilters?.groupBy === 'site'
      )
    }
  }, [headerFilters?.groupBy])

  const onRowGroupOpened = (params) => {
    // ignore open/close events from the ActionButtons
    if (headerFilters?.groupBy !== 'terminal' && params.node.uiLevel === 1) return
    if (!params.event) return

    // inner groups
    if (params.node.uiLevel === 1 && params.node.expanded && !expandedGroups.includes(params.node.parent.id)) {
      setExpandedGroups((prev) => [...prev, params.node.parent.id]) // add id to list of expanded groups
      return
    }
    if (params.node.uiLevel === 1 && !params.node.expanded) {
      let x = false
      params.node.parent?.childrenAfterGroup?.forEach((node) => {
        if (node.expanded) x = true // check if any other nodes in group are expanded
      })
      if (!x) {
        // if no others are expanded, remove group from list of expanded groups
        setExpandedGroups((prev) => prev.filter((y) => y !== params.node.parent.id))
        return
      }
    }

    // outer groups
    if (params.node.expanded && params.node.uiLevel === 0) {
      setIsExpanded(true) // expand all children
      params.node.childrenAfterGroup?.forEach((node) => node.setExpanded(true))
      setExpandedGroups((prev) => [...prev, params.node.id])
    } else {
      params.node.childrenAfterGroup?.forEach((node) => node.setExpanded(false))
      setExpandedGroups((prev) => prev.filter((x) => x !== params.node.id))
      if (expandedGroups.includes('all')) {
        // make a list of all expanded groups
        const newExpandedGroups = []
        params.api.forEachNode((node) => {
          if (node.expanded) {
            newExpandedGroups.push(node.id)
          }
        })
        setExpandedGroups(newExpandedGroups)

        let x = false
        params.api.forEachNodeAfterFilter((node) => {
          if (node.expanded) x = true
        })
        setIsExpanded(x)
      }
    }
  }

  return (
    <GraviGrid
      columnDefs={columnDefs}
      externalRef={gridAPIRef}
      columnApiRef={columnApiRef}
      controlBarProps={{
        title: 'Supply',
        setFilters: setSupplyDirectoryGridFilters,
        filters: supplyDirectoryGridFilters,
        serverParams: supplyDirectoryData?.params,
        hiddenFilterKeys: ['as_of'],
        actionButtons: (
          <PriceManagementActionButtons
            filters={supplyDirectoryGridFilters}
            isRefetching={isSupplyDirectoryRefetching}
            headerFilters={headerFilters}
            setHeaderFilters={handleUpdateHeaderFilters}
            gridAPIRef={gridAPIRef}
            handleEditOutage={openEditOutage}
            setExpandedGroups={setExpandedGroups}
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
          />
        ),
      }}
      loading={isSupplyDirectoryFetching}
      agPropOverrides={agPropOverrides}
      groupDefaultExpanded={-1}
      storageKey={SUPPLY_DIRECTORY_GRID_KEY}
      rowData={normalizedData}
      onRowGroupOpened={onRowGroupOpened}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${SUPPLY_DIRECTORY_GRID_KEY}`, config)}
      {...gridViewManager(SUPPLY_DIRECTORY_GRID_KEY, updateSetting)}
      updateEP={(row) => {
        const updatedRow = Array.isArray(row) ? row : [row]
        updatedRow.forEach((item) => {
          handleUpdateRow({ updatedRow: item, as_of: headerFilters?.filter?.as_of })
        })
      }}
    />
  )
}
